/**
 * System document mutations against ant: upload and archive.
 *
 * Uploading replaces admin_api's upload-url → PUT → confirm with ant's flow:
 * `requestDocumentUpload` signs a PUT for a new object key, the file goes
 * straight to the bucket with exactly the headers that came back, and
 * `createDocument` stores the document once ant finds the file there.
 * Archiving takes a document off the list; ant keeps the row and the file.
 * Only Movatic staff may do either: ant refuses everyone else.
 *
 * Note: Run `npm run relay` after making changes to GraphQL mutations
 * to regenerate the type artifacts.
 */
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import type { GraphQLTaggedNode, MutationParameters } from 'relay-runtime';
import { relayEnvironment } from '../../../../relay';
import type { useDocumentMutationsRequestUploadMutation as RequestUploadMutation } from './__generated__/useDocumentMutationsRequestUploadMutation.graphql';
import type { useDocumentMutationsCreateMutation as CreateMutation } from './__generated__/useDocumentMutationsCreateMutation.graphql';
import type { useDocumentMutationsArchiveMutation as ArchiveMutation } from './__generated__/useDocumentMutationsArchiveMutation.graphql';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const RequestUpload = graphql`
  mutation useDocumentMutationsRequestUploadMutation(
    $input: RequestDocumentUploadInput!
  ) {
    requestDocumentUpload(input: $input) {
      uploadUrl
      objectKey
      headers {
        name
        value
      }
    }
  }
`;

const Create = graphql`
  mutation useDocumentMutationsCreateMutation($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      errors {
        code
        fields
        message
        shortMessage
      }
      result {
        id
        name
      }
    }
  }
`;

const Archive = graphql`
  mutation useDocumentMutationsArchiveMutation($id: ID!) {
    archiveDocument(id: $id) {
      errors {
        code
        fields
        message
        shortMessage
      }
      result {
        id
        archivedAt
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface UploadDocumentInput {
  systemId: string;
  file: File;
  description?: string;
  category?: string;
}

type Created = NonNullable<CreateMutation['response']['createDocument']['result']>;
type Archived = NonNullable<ArchiveMutation['response']['archiveDocument']['result']>;

interface Payload<T> {
  readonly errors: ReadonlyArray<{ readonly message?: string | null }>;
  readonly result?: T | null;
}

// ============================================================================
// Imperative API
// ============================================================================

function commit<T extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  variables: T['variables']
): Promise<T['response']> {
  return new Promise((resolve, reject) => {
    commitMutation<T>(relayEnvironment, {
      mutation,
      variables,
      onCompleted: (response: T['response']) => resolve(response),
      onError: reject,
    });
  });
}

// Ash reports a refused write in the payload's `errors`, not as a GraphQL error.
function resultOf<T>(payload: Payload<T>): T {
  if (payload.errors.length > 0 || !payload.result) {
    const messages = payload.errors.map((error) => error.message).filter(Boolean);
    throw new Error(messages.join(', ') || 'The request failed');
  }
  return payload.result;
}

const TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

// What ant is told the file is: the browser's type, or its extension's when
// the browser has none.
function contentTypeOf(file: File): string {
  if (file.type === 'image/jpg') return 'image/jpeg';
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return TYPES_BY_EXTENSION[extension] ?? 'application/octet-stream';
}

export async function uploadDocument({
  systemId,
  file,
  description,
  category,
}: UploadDocumentInput): Promise<Created> {
  const contentType = contentTypeOf(file);
  const { requestDocumentUpload: slot } = await commit<RequestUploadMutation>(
    RequestUpload,
    { input: { systemId, contentType, byteSize: file.size } }
  );

  // The headers are signed into the URL: the bucket refuses a PUT without them.
  const upload = await fetch(slot.uploadUrl, {
    method: 'PUT',
    headers: Object.fromEntries(slot.headers.map(({ name, value }) => [name, value])),
    body: file,
  });
  if (!upload.ok) {
    throw new Error(`Upload failed with status ${upload.status}`);
  }

  const { createDocument } = await commit<CreateMutation>(Create, {
    input: {
      systemId,
      name: file.name,
      description: description || undefined,
      category: category || undefined,
      objectKey: slot.objectKey,
      contentType,
      byteSize: file.size,
    },
  });
  return resultOf(createDocument);
}

export async function archiveDocument(id: string): Promise<Archived> {
  const { archiveDocument: payload } = await commit<ArchiveMutation>(Archive, {
    id,
  });
  return resultOf(payload);
}

// ============================================================================
// Hooks
// ============================================================================

export function useUploadDocument(
  options?: Omit<UseMutationOptions<Created, Error, UploadDocumentInput>, 'mutationFn'>
) {
  return useMutation({ mutationFn: uploadDocument, ...options });
}

export function useArchiveDocument(
  options?: Omit<UseMutationOptions<Archived, Error, string>, 'mutationFn'>
) {
  return useMutation({ mutationFn: archiveDocument, ...options });
}
